import { DateFragment, TimezoneRegion } from "@tento-chrono";
import { AsyncStateManager } from "@tento-core/async-state-manager";
import { LiveData } from "@tento-core/livedata";
import { ComponentIcon, LonaIcons } from "@tento-ui/ui/icons";
import { Div } from "@tento-ui/component-builtin";
import {
  CalendarSubcolumn,
  type CalendarSubcolumnSpan,
} from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import {
  type BindCellContext,
  type RenderPlugin,
  type ContentProviders,
  type StaticChildSpec,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { Json, type JsonObject, type Option, RowId } from "@tento-lona/sheets";
import type { RowLocalId } from "@tento-lona/sheets";
import { type SheetRow } from "@tento-lona";
import { SheetViewModel } from "@tento-lona";
import { SpanDebugFlat } from "./data/span/span-debug-flat.ts";
import { DebugSleepSpan } from "./data/span/span-debug-sleep.ts";
import { DataLegend } from "./data/legend.ts";
import { DataStore } from "./data/data-store.ts";
import { SpanDebugChart } from "./data/span/span-debug-chart.ts";
import { SpanSleep } from "./data/span/span-sleep.ts";
import { SpanColumn } from "@tento-ui/components/span/components/span-column";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { $$ } from "@tento-ui/fastdom";
import { DATA_ALIAS_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import type { SheetBinder } from "../../sheet/sheet-index-renderer.ts";

/** The alias plugin for a data row: its chart and legend. */
export class DataAliasPlugin
  implements RenderPlugin.Alias<HTMLDivElement, DataAliasPlugin.Legend>
{
  static readonly spec: PerTypeSpec = DATA_ALIAS_SPEC;

  readonly role = "alias";
  readonly id = "alias.data";
  readonly aliases: readonly string[] = ["data"];
  readonly icon: HTMLTemplateElement = LonaIcons.$template(ComponentIcon);

  #stateManager = new AsyncStateManager<HTMLElement, unknown>();
  #titles = new Map<RowLocalId, DataTitleHeader>();

  readonly context: DataAliasPlugin.Context;

  readonly #patchRowAttributes: (
      rowId: RowId,
      attrs: JsonObject,
    ) => void;
  constructor(
    public readonly makeRowOptions: RowOptionsElement.Factory,
    public readonly renderDebug: LiveData.Flag.Readable,
    patchRowAttributes: (
      rowId: RowId,
      attrs: JsonObject,
    ) => void,
  ) {
    this.#patchRowAttributes = patchRowAttributes;
    this.context = {
      store: new DataStore(this.renderDebug),
      columns: new LiveData<
        Option<{
          sid: string;
          range: ColumnIndex.Key.Resolved.Default.Range;
        }>
      >(null),
      stateManager: this.#stateManager,
    };
  }

  isHidden(row: SheetRow.Joined.Handle): boolean {
    const dataJson = Json.child(Json.child(row.attributes(), "children"), "data-json");
    return Json.boolean(dataJson, "visible") === false;
  }

  setHidden(row: SheetRow.Joined.Handle, hidden: boolean): void {
    this.#patchRowAttributes(RowId.fromLocalId(row.id), {
      children: { "data-json": { visible: !hidden } },
    });
  }

  makeCell(): HTMLDivElement {
    return Div.make();
  }

  bindCell(_$cell: HTMLDivElement, _context: BindCellContext): void {}

  #calendarProvider: CalendarSubcolumn.Provider = ((self) => ({
    subcolumnFromRow(
      sheet: SheetViewModel.Surface<SheetBinder>,
      row: SheetRow.Joined.Handle,
      _parentRow: SheetRow.Joined.Handle,
    ): CalendarSubcolumn {
      const width = Json.string(row.attributes(), "width");
      const asChart = row.lookupKey !== ":pomodoro:timer";
      const isWhoopSleep =
        row.lookupKey?.includes("sleep") && row.lookupKey.includes("whoop");
      const isGarminSleep =
        row.lookupKey?.includes("sleep") && row.lookupKey.includes("garmin");

      let makeSpansForWindow: (
        windowed: DateFragment.Windowed,
        tz: TimezoneRegion,
      ) => Promise<CalendarSubcolumnSpan[]>;
      let makeLayout: () => HTMLElement = SpanColumn.make.bind(SpanColumn);

      if (isWhoopSleep) {
        makeSpansForWindow = SpanSleep.makeSpansFactory(self, sheet, row);
        makeLayout = SpanSleep.makeLayout;
      } else if (isGarminSleep) {
        makeSpansForWindow = DebugSleepSpan.makeSpansFactory(self, sheet, row);
        makeLayout = SpanSleep.makeLayout;
      } else if (asChart) {
        makeSpansForWindow = SpanDebugChart.makeSpansFactory(self, sheet, row);
      } else {
        makeSpansForWindow = SpanDebugFlat.makeSpansFactory(self, sheet, row);
      }

      return {
        rowId: RowId.fromLocalId(row.id),
        width: typeof width === "string"
          ? { type: "preferred", width }
          : { type: "flex" },
        makeSpans: ({ windowed, tz: tzr }) => makeSpansForWindow(windowed, tzr),
        makeLayout,
      };
    },
  }))(this);

  contentProviders: ContentProviders = {
    calendar: this.#calendarProvider,
  };

  legendFactory: SheetLegendClass<DataAliasPlugin.Legend> =
    DataAliasPlugin.Legend.factory;

  onBindRow(_handle: SheetDelegate, _row: SheetRow.Joined.Handle): void {}

  staticChildren(
    row: SheetRow.Joined.Handle,
    _handle: SheetDelegate,
  ): StaticChildSpec[] {
    let $title = this.#titles.get(row.id);
    if (!$title) {
      $title = DataTitleHeader.make();
      this.#titles.set(row.id, $title);
    }

    const title = row.label ?? row.lookupKey ?? "Untitled";
    $title.bind(title);

    return [{ id: "data-title", $element: $title, fillWidth: true }];
  }

  onResize(_$cell: HTMLDivElement, _context: BindCellContext): void {}

  onViewportColumnsChanged(
    _row: SheetRow.Joined.Handle,
    columns: ColumnIndex.Key.Resolved.Default.Range,
    rowList: SheetDelegate,
    _$legend: Option<DataAliasPlugin.Legend>,
  ): void {
    this.context.columns.update({
      sid: rowList.state.sheet.handle.network.id,
      range: columns,
    });
  }

  loadData = null;

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return null;
  }

  onRenderedColumnsWillChange(): void {}
}

/** Types and values that go with `DataAliasPlugin`: `Legend`, `Context`. */
export namespace DataAliasPlugin {
  export type Legend = DataLegend;
  export const Legend: typeof DataLegend = DataLegend;

  export type Context = {
    store: DataStore;
    columns: LiveData<
      Option<{
        sid: string;
        range: ColumnIndex.Key.Resolved.Default.Range;
      }>
    >;
    /**
     * Guards a cell element's async load: `bind($el, token)` answers whether
     * `token` is still the element's latest load (tokens compare by identity).
     */
    stateManager: AsyncStateManager<HTMLElement, unknown>;
  };
}

@component({
  name: "std-data-title-header",
})
export class DataTitleHeader extends LonaWebComponent {
  #$label: HTMLElement = this.$("label");

  bind(title: string): this {
    this.#$label.textContent = title;
    return this;
  }

  bindHeight(heightPx: number): void {
    $$.measure(() => {
      const height = this.offsetHeight;
      $$.mutate(() => {
        if (heightPx === height) return;
        this.style.height = heightPx + "px";
      });
    });
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        height: 32px;
      }

      #root {
        align-items: center;
        border-radius: 8px;
        padding: 8px 12px;
        background: var(--bg-surface-light);
        margin-left: 3px;
        width: calc(100% - 6px);
        overflow: hidden;
        height: calc(100% - 2px);
        margin-block: 1px;
      }

      #label {
        color: var(--text-sec);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        align-self: center;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-row id="root">
      <p id="label" class="small"></p>
    </std-row>
  `;
}
